import React, { useMemo, useState } from "react";
import { X } from "lucide-react";

/**
 * Combine two tabs: every row of `source` moves into the list picked here and
 * `source` is removed (Christian Gravias, Monday 13202407781: "Do the agents
 * have the ability to combine 2 lists?"). Before this it took Select all,
 * "+ Add / move", then deleting the empty tab.
 */
export default function MergeListModal({ source, lists, onClose, onConfirm }) {
  // Only lists of the same kind (owners/buyers), never the tab itself.
  const others = useMemo(
    () => lists.filter((l) => l.id !== source.id && (!l.view || l.view === source.view)),
    [lists, source]
  );
  // Grouped by category heading, like the Add / move picker, because every
  // suburb can have its own "2 Bedroom Unit" list.
  const grouped = useMemo(() => {
    const groups = [];
    const byTitle = new Map();
    others.forEach((l) => {
      const title = (l.group_name || "").trim();
      if (!byTitle.has(title)) {
        const g = { title, lists: [] };
        byTitle.set(title, g);
        groups.push(g);
      }
      byTitle.get(title).lists.push(l);
    });
    return groups.sort((a, b) => (a.title ? 1 : 0) - (b.title ? 1 : 0) || a.title.localeCompare(b.title));
  }, [others]);

  // Default to a list under the same heading: the usual case is two tabs of
  // one category ("Older Block Leads" into "Block Leads").
  const sameHeading = others.find(
    (l) => (l.group_name || "").trim() === (source.group_name || "").trim()
  );
  const [choice, setChoice] = useState(String((sameHeading || others[0])?.id ?? ""));
  const [busy, setBusy] = useState(false);
  const target = others.find((l) => String(l.id) === choice);
  const count = source.record_count || 0;

  const confirm = async () => {
    if (!target) return;
    setBusy(true);
    try {
      await onConfirm(target);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[80] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-[420px] max-w-full flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-bold text-gray-900">Merge "{source.name}" into another list</h3>
          <button onClick={onClose} className="text-gray-500">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-gray-400 font-bold">Merge into</span>
            <select
              value={choice}
              onChange={(e) => setChoice(e.target.value)}
              className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            >
              {!others.length && <option value="">No other lists yet</option>}
              {grouped.map((g) =>
                g.title ? (
                  <optgroup key={g.title} label={g.title}>
                    {g.lists.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </optgroup>
                ) : (
                  g.lists.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))
                )
              )}
            </select>
          </label>

          {target && (
            <p className="text-sm text-gray-600">
              All {count} {count === 1 ? "record" : "records"} in "{source.name}" will move into "
              {target.name}", and the "{source.name}" tab will be removed. Records already in both
              lists are not doubled up. Notes, colours and temperatures stay as they are.
            </p>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200">
          <button onClick={onClose} className="text-sm font-semibold text-gray-600">
            Cancel
          </button>
          <button
            onClick={confirm}
            disabled={busy || !target}
            className="px-4 py-2 rounded-lg bg-[#55c5d0] text-white text-sm font-bold disabled:opacity-50"
          >
            {busy ? "Merging…" : "Merge lists"}
          </button>
        </div>
      </div>
    </div>
  );
}
